/* ==================================================================
   Big Billion Days · mock campaign response

   Proposed contract for the `bigBillionDays` object the campaign API
   (APIHelper.topFive) will return alongside `independence` (Go For Gold).
   Until the backend ships it, api.js merges this object into the live
   topFive response (USE_MOCK). The page renders ONLY what is in here —
   copy, highlights, CTAs, progress, campaign end — so the backend can
   change content without a frontend deploy.

   Section visibility
   - `enabled`  : hard switch, the backend can pull a section any time.
   - `audience` : who sees the section, evaluated against `user`:
        ALL                        → everyone
        PHYSICAL_CARD_HOLDERS      → user.physicalCardHolder === true
        NON_PHYSICAL_CARD_HOLDERS  → user.physicalCardHolder === false

   Copy
   - `text` is the full sentence; `highlight` is the exact substring that
     is rendered in the gold gradient. When `highlight` is missing or not
     found in `text`, the sentence renders plain.

   CTA actions (dispatched to the native bridge by the page)
     ADD_MONEY | GET_CARD | BUY_GOLD | VOUCHERS | PAY_BILLS
   - optional `params` are forwarded to the native screen, e.g.
     PAY_BILLS + { categoryName: 'Credit Card' } opens screenBBPS on that category
   ================================================================== */

export const BBD_MOCK_RESPONSE = {
  bigBillionDays: {
    enabled: true,
    campaignStart: 1790101800000, // 2026-09-23T00:00:00+05:30
    campaignEnd: 1790965799000,   // 2026-10-02T23:59:59+05:30 — drives the countdown in the top banner

    banner: {
      text: 'Campaign is ending soon!',
    },

    user: {
      physicalCardHolder: false, // true → the "Get My Card" offer is not shown (flip here to preview)
    },

    /* 1 · everyone — the hero "Add money" offer is driven by the `firstCardLoad`
       milestone inside `welcomeMilestones.items`; the other items are ignored by
       this page. Visibility is gated by `eligible` (not `enabled`); it's true here
       so the offer renders in local preview — the live API controls it. */
    welcomeMilestones: {
      enabled: false,
      audience: 'ALL',
      title: 'Welcome SuperCoins',
      text: 'New to GoSats? Earn SuperCoins on each of your firsts.',
      eligible: true,
      eligibleFrom: 1790188200000,
      eligibleTill: 1797359399000,
      items: [
        {
          // completed:false here so the offer renders in local preview; when the
          // live API sends completed:true the section is hidden (reward credited).
          key: 'firstCardLoad',
          text: 'Load your card for the first time',
          rewardAmount: 50,
          minAmount: 100,
          completed: false,
          completedOn: null,
          orderId: null,
          cta: { text: 'Add Money', action: 'ADD_MONEY', enabled: true },
        },
        {
          key: 'firstCcRepayment',
          text: 'Pay your first credit card bill',
          rewardAmount: 50,
          minAmount: 100,
          completed: false,
          completedOn: null,
          orderId: null,
          cta: { text: 'Pay Bill', action: 'PAY_BILLS', params: { categoryName: 'Credit Card' }, enabled: true },
        },
        {
          key: 'firstVoucherPurchase',
          text: 'Buy your first voucher',
          rewardAmount: 50,
          minAmount: 100,
          completed: false,
          completedOn: null,
          orderId: null,
          cta: { text: 'View Vouchers', action: 'VOUCHERS', enabled: true },
        },
        {
          key: 'firstGoldPurchase',
          text: 'Buy gold for the first time',
          rewardAmount: 50,
          minAmount: 100,
          completed: false,
          completedOn: null,
          orderId: null,
          cta: { text: 'Buy Gold', action: 'BUY_GOLD', enabled: true },
        },
      ],
      completedCount: 1,
      totalCount: 4,
      totalReward: 200,
      earnedReward: 50,
    },

    /* 2 · non physical card holders only */
    physicalCard: {
      enabled: true,
      audience: 'NON_PHYSICAL_CARD_HOLDERS',
      text: 'Get the physical Visa platinum card worth ₹399 with your SuperCoins, earn rewards on every tap.',
      highlight: 'worth ₹399 with your SuperCoins',
      cta: { text: 'Get My Card', action: 'GET_CARD', enabled: true },
    },

    /* 3 · everyone */
    cardSpend: {
      enabled: true,
      audience: 'ALL',
      text: 'Spend more than ₹10k on Flipkart and get a 50 SuperCoin bonus.',
      highlight: 'get a 50 SuperCoin bonus.',
      spent: 0,            // ₹ spent on the GoSats card so far this campaign
      bonusThreshold: 10000,  // ₹ spend that unlocks the 50 SuperCoin bonus (middle label)
      trackMax: 25000,        // ₹ at the right end of the progress bar
      claimEnabled: false, // API sets true when the ₹10k reward is claimable — CTA then calls fk-spend-10k/claim
      cta: { text: 'Add Money', action: 'ADD_MONEY', enabled: true },
    },

    /* 4 · everyone */
    gold: {
      enabled: true,
      audience: 'ALL',
      text: 'Buy Gold and get 5% back in SuperCoins.',
      highlight: '5% back',
      cta: { text: 'Buy Gold', action: 'BUY_GOLD', enabled: true },
    },

    /* 5 · everyone */
    vouchers: {
      enabled: true,
      audience: 'ALL',
      text: 'Use SuperCoins to pay up to 60% on vouchers',
      highlight: '60% on vouchers',
      cta: { text: 'View More Vouchers', action: 'VOUCHERS', enabled: true },
    },

    /* 6 · everyone */
    bills: {
      enabled: true,
      audience: 'ALL',
      title: 'Pay Your Bills & Earn',
      cards: [
        {
          type: 'CREDIT_CARD', // picks the artwork: CREDIT_CARD | BBPS
          text: 'Use SuperCoins to pay up to 2% of your credit card bill and get back 0.5% in SuperCoins on every payment.',
          highlight: 'pay up to 2% of your credit card bill',
          cta: { text: 'Pay Bills', action: 'PAY_BILLS', params: { categoryName: 'Credit Card' }, enabled: true },
        },
        {
          type: 'BBPS',
          text: 'Use SuperCoins to pay up to 60% of your bills and get back 0.5% in SuperCoins on every payment.',
          highlight: 'pay up to 60% of your bills and get back 0.5% in SuperCoins',
          cta: { text: 'Pay Bills', action: 'PAY_BILLS', enabled: true },
        },
      ],
    },
  },
};
